import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon, ArrowPathIcon, ReceiptPercentIcon } from '@heroicons/react/24/outline';
import { useModal } from '../hooks/useModal';
import { useTableQuery } from '../hooks/useTableQuery';
import { useCrud } from '../hooks/useCrud';
import { useAsideContext } from '@contexts/AsideContext';
import { PageMeta, TableCard, ColumnDefinition } from '@common/components';
import { Button, Badge, ConfirmDialog, Select, Modal, Input } from '@common/components/ui';
import productService from '@services/productService';
import categoryService from '@services/categoryService';
import { Product } from '@services/core/types';
import ProductForm from '../components/products/ProductForm';
import toast from 'react-hot-toast';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

const Products = () => {
  const queryClient = useQueryClient();
  const { openAside, closeAside } = useAsideContext();
  const { isOpen: isDeleteOpen, open: openDeleteDialog, close: closeDeleteDialog } = useModal();
  const { isOpen: isBulkDiscountOpen, open: openBulkDiscountModal, close: closeBulkDiscountModal } = useModal();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxProduct, setLightboxProduct] = useState<Product | null>(null);

  const { handleDelete } = useCrud<Partial<Product>, Product>({
    queryKey: 'products',
    service: { remove: (id) => productService.remove(id) },
    messages: { deleted: 'Product deleted successfully' },
  });

  // Fetch products
  const { data: productsData, isLoading, error } = useTableQuery({
    queryKey: ['products', currentPage, pageSize, searchQuery, statusFilter],
    fetchFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('limit', String(pageSize));
      if (searchQuery) params.set('search', searchQuery);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      return productService.getAll(params);
    },
  });

  // Fetch categories for form
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      try {
        // Try tree endpoint first
        let response;
        try {
          response = await categoryService.getTree();
        } catch (treeError) {
          console.warn('Tree endpoint failed, trying getAll:', treeError);
          // Fallback to getAll
          const allResponse = await categoryService.getAll();
          response = {
            success: allResponse.success,
            data: allResponse.data.categories || allResponse.data.items || [],
          };
        }

        const categories = response.data || [];

        if (!Array.isArray(categories)) {
          console.error('Categories is not an array:', categories);
          toast.error('Invalid categories data format');
          return [];
        }

        // Flatten the tree to show all categories (including children)
        const flattenCategories = (cats: any[]): any[] => {
          const result: any[] = [];
          cats.forEach(cat => {
            result.push(cat);
            if (cat.children && cat.children.length > 0) {
              result.push(...flattenCategories(cat.children));
            }
          });
          return result;
        };

        return flattenCategories(categories);
      } catch (error: any) {
        console.error('Error fetching categories:', error);
        toast.error('Failed to load categories: ' + (error.response?.data?.message || error.message));
        return [];
      }
    },
  });

  // Finplus sync: the backend starts the sync in the background and responds
  // immediately (a full run can take minutes), so we poll for the real result instead
  // of trusting the POST response.
  const [isPollingFinplusSync, setIsPollingFinplusSync] = useState(false);
  const [isSyncingFinplus, setIsSyncingFinplus] = useState(false);

  const pollFinplusSyncStatus = async (toastId: string, attempt = 0) => {
    const maxAttempts = 100; // ~100 * 3s = 5 minutes
    try {
      const { data: status } = await productService.getFinplusSyncStatus();

      if (status.inProgress) {
        if (attempt >= maxAttempts) {
          setIsPollingFinplusSync(false);
          toast.error('Finplus sync is taking longer than expected — check back later', { id: toastId });
          return;
        }
        setTimeout(() => pollFinplusSyncStatus(toastId, attempt + 1), 3000);
        return;
      }

      setIsPollingFinplusSync(false);
      queryClient.invalidateQueries({ queryKey: ['products'] });

      if (status.lastError) {
        toast.error(`Finplus sync failed: ${status.lastError}`, { id: toastId });
        return;
      }

      const result = status.lastResult;
      toast.success(
        result
          ? `Finplus sync complete: ${result.barcodesOutOfStock} of ${result.barcodesChecked} barcodes now out of stock` +
              (result.errors.length ? ` (${result.errors.length} errors)` : '')
          : 'Finplus sync complete',
        { id: toastId }
      );
    } catch (error) {
      setIsPollingFinplusSync(false);
      toast.error('Lost track of Finplus sync status — check back later', { id: toastId });
    }
  };

  const handleSyncFinplus = async () => {
    setIsSyncingFinplus(true);
    try {
      await productService.syncFinplus();
      setIsPollingFinplusSync(true);
      const toastId = toast.loading('Finplus sync running in the background...');
      pollFinplusSyncStatus(toastId);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to start Finplus sync');
    } finally {
      setIsSyncingFinplus(false);
    }
  };

  // Bulk discount: same fire-and-poll shape as Finplus sync above - the backend runs
  // the discount pass in the background and responds immediately.
  const [bulkDiscountPercentage, setBulkDiscountPercentage] = useState('');
  const [isApplyingBulkDiscount, setIsApplyingBulkDiscount] = useState(false);
  const [isPollingBulkDiscount, setIsPollingBulkDiscount] = useState(false);

  const pollBulkDiscountStatus = async (toastId: string, attempt = 0) => {
    const maxAttempts = 100; // ~100 * 3s = 5 minutes
    try {
      const { data: status } = await productService.getBulkDiscountStatus();

      if (status.inProgress) {
        if (attempt >= maxAttempts) {
          setIsPollingBulkDiscount(false);
          toast.error('Bulk discount is taking longer than expected — check back later', { id: toastId });
          return;
        }
        setTimeout(() => pollBulkDiscountStatus(toastId, attempt + 1), 3000);
        return;
      }

      setIsPollingBulkDiscount(false);
      queryClient.invalidateQueries({ queryKey: ['products'] });

      if (status.lastError) {
        toast.error(`Bulk discount failed: ${status.lastError}`, { id: toastId });
        return;
      }

      const result = status.lastResult;
      toast.success(
        result
          ? `Bulk discount complete: ${result.productsUpdated} of ${result.productsEligible} products updated` +
              (result.productsSkipped ? `, ${result.productsSkipped} left untouched (bigger existing discount)` : '') +
              (result.errors.length ? ` (${result.errors.length} errors)` : '')
          : 'Bulk discount complete',
        { id: toastId }
      );
    } catch (error) {
      setIsPollingBulkDiscount(false);
      toast.error('Lost track of bulk discount status — check back later', { id: toastId });
    }
  };

  const handleApplyBulkDiscount = async () => {
    const percentage = Number(bulkDiscountPercentage);
    if (!bulkDiscountPercentage || !Number.isFinite(percentage) || percentage <= 0 || percentage > 100) {
      toast.error('Enter a percentage greater than 0 and up to 100');
      return;
    }

    setIsApplyingBulkDiscount(true);
    try {
      await productService.applyBulkDiscount(percentage);
      closeBulkDiscountModal();
      setBulkDiscountPercentage('');
      setIsPollingBulkDiscount(true);
      const toastId = toast.loading('Applying bulk discount in the background...');
      pollBulkDiscountStatus(toastId);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to start bulk discount');
    } finally {
      setIsApplyingBulkDiscount(false);
    }
  };

  const handleFormSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['products'] });
    closeAside();
  };

  const openProductForm = (product: Product | null) => {
    openAside(
      <ProductForm
        product={product}
        categories={categoriesData || []}
        onSuccess={handleFormSuccess}
        onCancel={closeAside}
      />,
      {
        title: product ? 'Edit Product' : 'Create Product',
        width: '800px',
      }
    );
  };

  const handleDeleteClick = (id: string) => {
    setProductToDelete(id);
    openDeleteDialog();
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, 'success' | 'warning' | 'error' | 'gray'> = {
      active: 'success',
      draft: 'warning',
      inactive: 'gray',
    };
    return <Badge color={statusMap[status] || 'gray'}>{status.toUpperCase()}</Badge>;
  };

  const formatPrice = (price: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price);
  };

  const products = productsData?.data.products || [];
  const total = productsData?.data.total || 0;
  const totalPages = productsData?.data.totalPages || 1;

  const columns: ColumnDefinition<Product>[] = [
    {
      name: 'image',
      title: 'Image',
      value: (product) => (
        <div
          className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 cursor-pointer hover:opacity-90 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            if (product.images?.[0]?.src) {
              setLightboxProduct(product);
              setLightboxOpen(true);
            }
          }}
        >
          {product.images?.[0]?.src ? (
            <img
              src={product.images[0].src}
              alt={product.images[0].alt || product.title}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-gray-400 text-xs">
              No image
            </div>
          )}
        </div>
      ),
    },
    {
      name: 'product',
      title: 'Product',
      value: (product) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{product.title}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">SKU: {product.sku}</div>
        </div>
      ),
    },
    {
      name: 'price',
      title: 'Price',
      value: (product) => (
        <span className="font-medium text-gray-900 dark:text-white">
          {formatPrice(product.price, product.currency)}
        </span>
      ),
    },
    {
      name: 'stock',
      title: 'Stock',
      value: (product) => (
        <span className="font-medium text-gray-900 dark:text-white">
          {product.inventory?.quantity || 0}
        </span>
      ),
    },
    {
      name: 'status',
      title: 'Status',
      value: (product) => getStatusBadge(product.status),
    },
  ];

  return (
    <>
      <PageMeta
        title="Products"
        breadcrumbs={[{ label: 'Products' }]}
        action={
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleSyncFinplus}
              loading={isSyncingFinplus || isPollingFinplusSync}
              icon={<ArrowPathIcon className="h-5 w-5" />}
            >
              Sync Finplus
            </Button>
            <Button
              variant="outline"
              onClick={openBulkDiscountModal}
              loading={isPollingBulkDiscount}
              icon={<ReceiptPercentIcon className="h-5 w-5" />}
            >
              Bulk Discount
            </Button>
            <Button onClick={() => openProductForm(null)} icon={<PlusIcon className="h-5 w-5" />}>
              Add Product
            </Button>
          </div>
        }
      />

      <TableCard
        columns={columns}
        data={products}
        loading={isLoading}
        error={error ? 'Failed to load products' : undefined}
        emptyMessage="No products found"
        searchable
        searchPlaceholder="Search products..."
        onSearch={(query) => {
          setSearchQuery(query);
          setCurrentPage(1);
        }}
        toolbar={
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'draft', label: 'Draft' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            fullWidth={false}
            className="w-40"
          />
        }
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onRowClick={openProductForm}
        rowActions={(product) => (
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openProductForm(product);
              }}
              className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(product.id);
              }}
              className="text-danger-600 hover:text-danger-900 dark:text-danger-400 dark:hover:text-danger-300"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          closeDeleteDialog();
          setProductToDelete(null);
        }}
        onConfirm={async () => {
          if (!productToDelete) return;
          await handleDelete(productToDelete);
          setProductToDelete(null);
        }}
        title="Delete Product"
        description="Are you sure you want to delete this product? This action cannot be undone."
        confirmLabel="Delete"
        successMessage="Product deleted"
      />

      {/* Bulk Discount */}
      <Modal
        isOpen={isBulkDiscountOpen}
        onClose={() => {
          closeBulkDiscountModal();
          setBulkDiscountPercentage('');
        }}
        title="Apply Bulk Discount"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Applies a % discount to every in-stock product whose current discount (if any) is
            smaller than this percentage. Products with an equal or bigger existing discount,
            and out-of-stock products, are left untouched.
          </p>
          <Input
            label="Discount percentage"
            type="number"
            min={1}
            max={100}
            step={1}
            value={bulkDiscountPercentage}
            onChange={(e) => setBulkDiscountPercentage(e.target.value)}
            placeholder="20"
            required
          />
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                closeBulkDiscountModal();
                setBulkDiscountPercentage('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleApplyBulkDiscount} loading={isApplyingBulkDiscount}>
              Apply Discount
            </Button>
          </div>
        </div>
      </Modal>

      {/* Image Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => {
          setLightboxOpen(false);
          setLightboxProduct(null);
        }}
        slides={
          lightboxProduct?.images?.map((img) => ({
            src: img.src,
            alt: img.alt || lightboxProduct.title,
          })) || []
        }
      />
    </>
  );
};

export default Products;
